import { createTheme } from '@material-ui/core/styles';
import hexToRgba from 'hex-rgba';

const base$1 = {
  brand: {
    default: '#7A3BFF',
    hover: '#B9A6FF',
    bg: '#E9E4FF',
    typography: '#7A3BFF',
    contrastText: '#FFFFFF'
  },
  neutral: {
    black: '#333333',
    gray800: '#999999',
    gray500: '#C0C0C0',
    gray200: '#E0E0E0',
    gray100: '#EFEFEF',
    gray50: '#F9F9F9',
    white: '#FFFFFF'
  },
  state: {
    error: '#E70404',
    success: '#2BB75D',
    ended: '#1EA079',
    infoText: '#177484',
    edit: '#21B5CD',
    pending: '#F1C036',
    pendingText: '#DEA912',
    successBg: '#F5F8E5',
    warningBg: '#FDF8EA',
    infoBg: '#E8F7FA',
    errorBg: '#FDEEEC'
  },
  function: {
    maPContent: '#9A66CC',
    maPBg: '#F9F6FC',
    maOContent: '#E37402',
    maOBg: '#FDF7F0',
    maYContent: '#DEA912',
    maYBg: '#FEFBF3',
    maGContent: '#9CC100',
    maGBg: '#FBFCF0',
    maBContent: '#21B5CD',
    maBBg: '#F2FBFC'
  },
  channel: {
    line: '#2BB700',
    instagram: '#F03755',
    messenger: '#007AFF',
    whatsapp: '#25D366',
    livechat: '#333333'
  }
};
const insightark$1 = {
  ...base$1,
  base: base$1,
  brand: {
    default: '#FF8C53',
    hover: '#FFA971',
    bg: '#FFECE5',
    typography: '#FF7734',
    contrastText: base$1.neutral.white
  }
};
const messagehero$1 = {
  ...base$1,
  base: base$1,
  brand: {
    default: '#E0E722',
    hover: '#CED264',
    bg: '#F3F6B5',
    typography: '#939F12',
    contrastText: base$1.neutral.black
  }
};
var color = {
  base: {
    base: base$1,
    ...base$1
  },
  insightark: insightark$1,
  messagehero: messagehero$1
};

const fontWeight = {
  l: 700,
  m: 500,
  s: 400
};
const fontSize = {
  xl: 24,
  l: 17,
  m: 15,
  s: 13,
  xs: 11
};
const fontFamily = "'Inter', 'Roboto', 'Noto Sans',  'Noto Sans TC', sans-serif";
const LINE_HEIGHT = 1.6;
const font = {
  dataNumber: {
    fontFamily,
    fontSize: fontSize.xl,
    fontWeight: fontWeight.l,
    lineHeight: LINE_HEIGHT
  },
  headline: {
    fontFamily,
    fontSize: fontSize.l,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT
  },
  'headline-bold': {
    fontFamily,
    fontSize: fontSize.l,
    fontWeight: fontWeight.l,
    lineHeight: LINE_HEIGHT
  },
  title: {
    fontFamily,
    fontSize: fontSize.m,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT
  },
  'title-bold': {
    fontFamily,
    fontSize: fontSize.m,
    fontWeight: fontWeight.l,
    lineHeight: LINE_HEIGHT
  },
  'title-underline': {
    fontFamily,
    fontSize: fontSize.m,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT,
    textDecoration: 'underline'
  },
  'content-m': {
    fontFamily,
    fontSize: fontSize.s,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT
  },
  'content-m-bold': {
    fontFamily,
    fontSize: fontSize.s,
    fontWeight: fontWeight.l,
    lineHeight: LINE_HEIGHT
  },
  'content-m-underline': {
    fontFamily,
    fontSize: fontSize.s,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT,
    textDecoration: 'underline'
  },
  'content-s': {
    fontFamily,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT
  },
  'content-s-bold': {
    fontFamily,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.l,
    lineHeight: LINE_HEIGHT
  },
  'content-s-underline': {
    fontFamily,
    fontSize: fontSize.xs,
    fontWeight: fontWeight.s,
    lineHeight: LINE_HEIGHT,
    textDecoration: 'underline'
  }
};

const oldColor = {
  $1: '#B5D100',
  $2: '#9CC100',
  $3: '#2A394D',
  $4: '#364355',
  $5: '#253346',
  $6: '#000000',
  $7: '#FFFFFF',
  $8: '#EFEFEF',
  $9: '#E0E0E0',
  $10: '#BDBDBD',
  $11: '#989898',
  $12: '#757575',
  $13: '#4C4C4C',
  $14: '#212121',
  $15: '#21B5CD',
  $16: '#F1C036',
  $17: '#E95445',
  $18: '#DEA912',
  $19: 'rgba(224, 224, 224, 0.2)',
  $20: '#F8FBE6',
  $21: '#F0F6CC',
  $22: '#FCFCFC',
  $23: '#F9F9F9'
};
const createColorfulTheme = type => {
  const themeColor = color[type];
  return createTheme({
    palette: {
      primary: {
        main: themeColor.brand.default,
        contrastText: themeColor.brand.contrastText
      }
    },
    overrides: {
      MuiButton: {
        label: {
          color: 'inherit'
        },
        root: {
          ...font['content-m'],
          textTransform: 'none',
          '&.Mui-disabled': {
            color: themeColor.neutral.gray200
          }
        },
        contained: {
          border: `1px solid transparent`,
          color: themeColor.neutral.white,
          backgroundColor: themeColor.neutral.black,
          boxShadow: 'none',
          '&:hover': {
            color: themeColor.brand.contrastText,
            backgroundColor: themeColor.brand.default
          },
          '&:active, &:hover': {
            boxShadow: 'none'
          }
        },
        outlined: {
          color: themeColor.neutral.black,
          borderColor: themeColor.neutral.black,
          backgroundColor: 'transparent',
          '&:hover': {
            color: themeColor.brand.typography,
            borderColor: themeColor.brand.default,
            backgroundColor: 'transparent'
          }
        },
        text: {
          backgroundColor: 'transparent',
          color: themeColor.brand.typography,
          '&:hover': {
            backgroundColor: 'transparent',
            color: themeColor.brand.hover
          }
        }
      },
      MuiBadge: {
        colorPrimary: {
          backgroundColor: themeColor.state.error
        }
      },
      MuiListItem: {
        root: {
          '&.Mui-selected': {
            color: themeColor.brand.contrastText,
            backgroundColor: themeColor.brand.default,
            '&:hover': {
              color: themeColor.brand.contrastText,
              backgroundColor: themeColor.brand.hover
            }
          },
          '&:hover': {
            color: themeColor.brand.contrastText,
            backgroundColor: themeColor.brand.hover
          }
        },
        button: {
          '&:hover': {
            color: themeColor.brand.contrastText,
            backgroundColor: themeColor.brand.hover
          }
        }
      },
      MuiInputBase: {
        root: {
          lineHeight: 'inherit'
        }
      },
      MuiMenuItem: {
        root: {
          color: themeColor.neutral.black,
          '&:hover': {
            color: themeColor.neutral.black,
            backgroundColor: themeColor.brand.bg
          }
        }
      },
      MuiSwitch: {
        root: {
          width: 41,
          height: 28,
          padding: '7px 6px'
        },
        colorPrimary: {
          padding: '4px',
          '&.Mui-checked + .MuiSwitch-track': {
            opacity: 1,
            backgroundColor: themeColor.brand.bg
          }
        },
        switchBase: {
          color: themeColor.neutral.gray200,
          transform: 'translateX(0px)',
          '&.Mui-checked': {
            transform: 'translateX(15px)'
          }
        },
        track: {
          backgroundColor: hexToRgba(themeColor.neutral.gray200, 50),
          opacity: 'inherit'
        }
      },
      MuiPickersStaticWrapper: {
        staticWrapperRoot: {
          borderRadius: '4px',
          boxShadow: '0 16px 48px 0 rgba(0, 0, 0, 0.07), 0 6px 18px 0 rgba(0, 0, 0, 0.03)'
        }
      },
      MuiPickersToolbar: {
        toolbar: {
          '& h4': {
            fontSize: '34px',
            color: themeColor.neutral.white,
            lineHeight: '52px'
          },
          '& h6': {
            fontSize: '16px',
            color: themeColor.neutral.white
          }
        }
      },
      MuiPickersSlideTransition: {
        transitionContainer: {
          '& p': {
            fontSize: '16px',
            lineHeight: '23px'
          }
        }
      }
    },
    button: {
      disabled: {
        color: oldColor.$10,
        backgroundColor: oldColor.$8,
        border: `1px solid ${oldColor.$9}`,
        cursor: 'not-allowed'
      },
      primaryBlue: {
        default: {
          fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
          fontStyle: 'normal',
          letterSpacing: '0px',
          lineHeight: '15px',
          color: oldColor.$3,
          fontSize: 15,
          fontWeight: '400',
          height: '44px',
          backgroundColor: oldColor.$7,
          border: `1px solid ${oldColor.$3}`,
          borderRadius: '22px',
          textTransform: 'none'
        },
        hover: {
          color: oldColor.$7,
          backgroundColor: oldColor.$3
        },
        spinner: {
          default: {
            color: oldColor.$3,
            width: '20px !important',
            height: '20px !important'
          },
          hover: {
            color: oldColor.$3
          }
        },
        spinnerHover: {
          color: oldColor.$7
        }
      },
      primaryBlueHighLight: {
        default: {
          fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
          fontStyle: 'normal',
          letterSpacing: '0px',
          lineHeight: '15px',
          color: oldColor.$7,
          fontSize: 15,
          fontWeight: '400',
          height: '44px',
          backgroundColor: oldColor.$3,
          borderRadius: '22px',
          textTransform: 'none'
        },
        hover: {
          backgroundColor: hexToRgba(oldColor.$4, 70)
        },
        spinner: {
          default: {
            color: oldColor.$8,
            width: '20px !important',
            height: '20px !important'
          },
          hover: {
            color: oldColor.$7
          }
        },
        spinnerHover: {
          color: oldColor.$7
        }
      },
      primaryHighlight: {
        default: {
          height: '44px',
          color: oldColor.$3,
          backgroundColor: oldColor.$7,
          border: `2px solid ${oldColor.$3}`,
          borderRadius: '22px',
          textTransform: 'none'
        },
        hover: {
          color: oldColor.$7,
          backgroundColor: oldColor.$3
        },
        spinner: {
          default: {
            color: oldColor.$3,
            width: '20px !important',
            height: '20px !important'
          },
          hover: {
            color: oldColor.$7
          }
        }
      },
      primaryGreen: {
        default: {
          fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
          fontStyle: 'normal',
          letterSpacing: '0px',
          lineHeight: '15px',
          fontSize: 15,
          fontWeight: '400',
          height: '44px',
          color: oldColor.$7,
          backgroundColor: oldColor.$1,
          borderRadius: '22px',
          textTransform: 'none'
        },
        hover: {
          backgroundColor: oldColor.$2,
          cursor: 'pointer'
        },
        spinner: {
          color: oldColor.$7,
          width: '20px !important',
          height: '20px !important'
        }
      },
      primaryGrey: {
        default: {
          fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
          fontStyle: 'normal',
          letterSpacing: '0px',
          lineHeight: '15px',
          fontSize: 15,
          fontWeight: '400',
          height: '44px',
          color: oldColor.$12,
          backgroundColor: oldColor.$7,
          border: `1px solid ${oldColor.$12}`,
          borderRadius: '22px',
          textTransform: 'none'
        },
        hover: {
          color: oldColor.$7,
          backgroundColor: oldColor.$12
        }
      },
      IconPrimaryGrey: {
        default: {
          height: '44px',
          padding: '10px 20px 10px 16px',
          color: oldColor.$11,
          backgroundColor: 'rgba(224, 224, 224, 0.2)',
          borderRadius: '22px',
          fontSize: '15px',
          lineHeight: 1,
          '& img': {
            display: 'inline-block',
            marginRight: '4px'
          }
        },
        hover: {
          backgroundColor: 'rgba(224, 224, 224, 0.5)'
        }
      }
    },
    typography: {
      fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
      fontStyle: 'normal',
      letterSpacing: '0px',
      fontWeight: 400,
      color: oldColor.$10,
      fontSize: 11,
      headline: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: '48px',
        color: oldColor.$2,
        fontSize: 48,
        fontWeight: '200'
      },
      title: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: '28px',
        color: oldColor.$3,
        fontSize: 28,
        fontWeight: '400'
      },
      display1: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: '24px',
        color: oldColor.$12,
        fontSize: 24,
        fontWeight: '700'
      },
      display2: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: '20px',
        color: oldColor.$3,
        fontSize: 20,
        fontWeight: '400'
      },
      display3: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: 1.4,
        color: oldColor.$3,
        fontSize: 17,
        fontWeight: '400'
      },
      subheading: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: '15px',
        color: oldColor.$3,
        fontSize: 15,
        fontWeight: '400'
      },
      subtitle1: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        lineHeight: 1.4,
        color: oldColor.$3,
        fontSize: 15,
        fontWeight: '400'
      },
      tooltip: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: 'normal',
        color: oldColor.$7,
        fontStretch: 'normal',
        fontSize: 13
      },
      h1: {
        color: oldColor.$2,
        fontSize: '48px'
      },
      h2: {
        color: oldColor.$3,
        fontSize: '28px'
      },
      h3: {
        lineHeight: 'normal',
        color: oldColor.$12,
        fontSize: '24px',
        fontWeight: 700
      },
      h4: {
        color: oldColor.$3,
        fontSize: '20px'
      },
      h5: {
        color: oldColor.$3,
        fontSize: '17px'
      },
      h6: {
        color: oldColor.$3,
        fontSize: '15px'
      },
      body1: {
        color: oldColor.$13,
        fontSize: '13px'
      },
      body2: {
        color: oldColor.$12,
        fontSize: '11px'
      }
    },
    input: {
      default: {
        wrapper: {
          border: 'solid 1px #bdbdbd',
          borderRadius: '22px',
          '&.valid, &.focus': {
            border: '1px solid  #b5d100'
          },
          '&.invalid': {
            border: '1px solid red'
          }
        },
        root: {},
        input: {
          backgroundColor: '#ffffff',
          color: oldColor.$3,
          fontSize: 15
        }
      }
    },
    dropdown: {
      default: {
        root: {
          position: 'relative',
          cursor: 'pointer',
          flex: 1
        },
        toggle: {
          display: 'flex',
          flex: 1,
          alignItems: 'center',
          justifyContent: 'flex-start',
          padding: '0 0 0 24px',
          border: 'solid 1px #bdbdbd',
          borderRadius: '20px',
          backgroundColor: '#ffffff',
          height: '40px',
          fontSize: 15
        },
        menu: {
          position: 'absolute',
          left: 0,
          right: 0,
          top: 44,
          zIndex: 999
        }
      }
    },
    iconText: {
      primaryGreen: {
        default: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          color: oldColor.$2
        },
        hover: {
          color: hexToRgba(oldColor.$2, 50)
        }
      },
      primaryGrey: {
        default: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          color: oldColor.$10
        },
        hover: {
          color: hexToRgba(oldColor.$10, 50)
        }
      }
    },
    link: {
      primaryGreen: {
        default: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          color: oldColor.$2,
          textDecoration: 'underline',
          '&:hover': {
            color: hexToRgba(oldColor.$2, 50)
          }
        }
      },
      primaryGrey: {
        default: {
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          cursor: 'pointer',
          color: oldColor.$10,
          textDecoration: 'underline',
          '&:hover': {
            color: hexToRgba(oldColor.$10, 50)
          }
        }
      }
    },
    badge: {
      dot: {
        top: 0,
        height: '8px',
        padding: 0,
        minWidth: '8px'
      },
      colorPrimary: {
        backgroundColor: oldColor.$17
      },
      colorSecondary: {
        backgroundColor: oldColor.$16
      }
    },
    chip: {
      root: {
        fontFamily: "'PingFang TC', 'Noto Sans TC', 'Helvetica Neue', Arial, sans-serif",
        fontStyle: 'normal',
        letterSpacing: '0px',
        color: oldColor.$7,
        fontSize: 13,
        fontWeight: '400',
        height: '24px'
      },
      label: {
        padding: 0
      },
      colorPrimary: {
        backgroundColor: oldColor.$1,
        '&:hover, &:focus': {
          backgroundColor: oldColor.$2
        }
      },
      colorSecondary: {
        color: oldColor.$7,
        backgroundColor: oldColor.$16,
        '&:hover, &:focus': {
          backgroundColor: oldColor.$18
        }
      },
      black: {
        backgroundColor: oldColor.$12,
        '&:hover, &:focus': {
          backgroundColor: oldColor.$11
        }
      }
    },
    toast: {
      anchorOriginBottomCenter: {
        bottom: '8px',
        position: 'absolute'
      }
    },
    toastContent: {
      root: {
        borderRadius: '50px',
        backgroundColor: oldColor.$17
      },
      second: {
        borderRadius: '50px',
        backgroundColor: '#ff9800'
      }
    },
    snackbar: {
      anchorOriginTopLeft: {
        top: '24px',
        left: '24px'
      },
      success: {
        backgroundColor: oldColor.$1
      },
      error: {
        backgroundColor: oldColor.$17
      },
      info: {
        backgroundColor: oldColor.$1
      },
      warning: {
        backgroundColor: oldColor.$16
      }
    },
    snackbarContent: {
      root: {
        borderRadius: '8px',
        paddingTop: '0px',
        paddingLeft: '24px',
        paddingBottom: '0px',
        minHeight: '48px',
        width: '100%'
      }
    },
    checkbox: {
      root: {
        padding: '5px',
        '&:hover': {
          backgroundColor: 'transparent'
        }
      },
      colorPrimary: {
        '&:hover': {
          backgroundColor: 'transparent'
        }
      }
    },
    color: themeColor,
    font,
    fontFamily,
    fontSize,
    fontWeight
  });
};
const insightark = createColorfulTheme('insightark');
const messagehero = createColorfulTheme('messagehero');
const base = createColorfulTheme('base');

export { base, color, font, fontFamily, fontSize, fontWeight, insightark, messagehero, oldColor };
